import { HiOutlineShieldCheck, HiOutlineShieldExclamation } from "react-icons/hi";
import { MdOutlineWarningAmber } from "react-icons/md";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import Enderecos from "../../common/query/Enderecos";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import { formatDocument } from "../../../utils/utils";

const situacaoUi = (situacao = "") => {
  const s = situacao.toString().toUpperCase();
  if (s.includes("REGULAR")) {
    return {
      label: situacao || "Indisponível",
      icon: <HiOutlineShieldCheck className="text-green-700" size={18} />,
      classes: "bg-green-100 text-green-700 border-green-200",
    };
  }
  if (!s) {
    return {
      label: "Indisponível",
      icon: <HiOutlineShieldExclamation className="text-gray-500" size={18} />,
      classes: "bg-gray-100 text-gray-600 border-gray-200",
    };
  }
  return {
    label: situacao,
    icon: <HiOutlineShieldExclamation className="text-red-700" size={18} />,
    classes: "bg-red-100 text-red-700 border-red-200",
  };
};

const RelacionamentoTable = ({ titulo, icon, headers, rows, renderRow }) => (
  <>
    <TitleStripe>
      {icon} {titulo}
    </TitleStripe>
    {rows?.length > 0 ? (
      <div className="relative rounded-2xl border border-gray-200 overflow-hidden">
        <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
          <div
            className="grid gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider"
            style={{ gridTemplateColumns: `repeat(${headers.length}, minmax(0, 1fr))` }}
          >
            {headers.map((h, i) => (
              <div key={i} className={i === 0 ? "" : "text-center"}>
                {h}
              </div>
            ))}
          </div>
        </div>
        <div>
          {rows.map((row, index) => (
            <div
              key={index}
              className={`grid gap-2 px-4 py-3 text-sm items-center ${
                index % 2 === 0 ? "bg-white" : "bg-gray-50"
              }`}
              style={{ gridTemplateColumns: `repeat(${headers.length}, minmax(0, 1fr))` }}
            >
              {renderRow(row)}
            </div>
          ))}
        </div>
      </div>
    ) : (
      <NoOcurrence />
    )}
  </>
);

const ValidaPFQuery = ({ data }) => {
  const dadosCadastrais = data?.dadosCadastrais;
  const situacao = situacaoUi(dadosCadastrais?.situacao);
  const isPEP = Boolean(dadosCadastrais?.pessoaPoliticamenteExposta);
  const temObito = Boolean(data?.obito);

  return (
    <>
      <TitleStripe>🪪 Validação de Identidade</TitleStripe>

      <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
        <div className="bg-gradient-to-r from-blue-50 to-white px-6 py-5 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">
              Nome completo
            </p>
            <p className="text-xl font-semibold text-gray-900">
              {dadosCadastrais?.nome || "Indisponível"}
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium ${situacao.classes}`}
          >
            {situacao.icon}
            {situacao.label}
          </span>
        </div>

        <div className="px-6 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 text-base">
            <div>
              <p className="text-gray-500 font-medium">CPF</p>
              <p className="text-gray-800">
                {formatDocument(dadosCadastrais?.cpf) || "Indisponível"}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium">Data de nascimento</p>
              <p className="text-gray-800">
                {dadosCadastrais?.dataNascimento || "Indisponível"}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium">Nome da mãe</p>
              <p className="text-gray-800">
                {dadosCadastrais?.nomeMae || "Indisponível"}
              </p>
            </div>
          </div>

          {isPEP && (
            <div className="mt-5 flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
              <MdOutlineWarningAmber className="text-yellow-700 flex-shrink-0" size={20} />
              <p className="text-yellow-800 font-semibold text-sm">
                Pessoa Politicamente Exposta (PEP)
              </p>
            </div>
          )}

          {temObito && (
            <div className="mt-3 flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <HiOutlineShieldExclamation className="text-red-700 flex-shrink-0" size={20} />
              <p className="text-red-800 font-semibold text-sm">
                Óbito constatado: {data.obito}
              </p>
            </div>
          )}
        </div>
      </div>

      {data?.enderecos && (
        <div>
          <TitleStripe>📍 Endereços</TitleStripe>
          {data.enderecos.length > 0 ? (
            <Enderecos enderecos={data.enderecos} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.telefones && (
        <div>
          <TitleStripe>☎️ Telefones</TitleStripe>
          {data.telefones.length > 0 ? (
            <Telefones telefones={data.telefones} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.emails && (
        <div>
          <TitleStripe>📧 E-mails</TitleStripe>
          {data.emails.length > 0 ? <Emails emails={data.emails} /> : <NoOcurrence />}
        </div>
      )}

      {data?.pessoasRelacionadas && (
        <RelacionamentoTable
          titulo="Pessoas Relacionadas"
          icon="👥"
          headers={["Nome", "CPF", "Grau de Parentesco"]}
          rows={data.pessoasRelacionadas}
          renderRow={(p) => (
            <>
              <div className="text-gray-900">{p?.nome || "-"}</div>
              <div className="text-center text-gray-700">
                {formatDocument(p?.cpf) || "-"}
              </div>
              <div className="text-center text-gray-700">
                {p?.grauParentesco || "-"}
              </div>
            </>
          )}
        />
      )}

      {data?.residencia && (
        <RelacionamentoTable
          titulo="Pessoas da Residência"
          icon="🏠"
          headers={["Nome", "CPF", "Idade", "Sexo"]}
          rows={data.residencia}
          renderRow={(p) => (
            <>
              <div className="text-gray-900">{p?.nome || "-"}</div>
              <div className="text-center text-gray-700">
                {formatDocument(p?.cpf) || "-"}
              </div>
              <div className="text-center text-gray-700">{p?.idade ?? "-"}</div>
              <div className="text-center text-gray-700">{p?.sexo || "-"}</div>
            </>
          )}
        />
      )}
    </>
  );
};

export default ValidaPFQuery;
